// src/Components/BCM_Norms/SideNav.js

import React from "react";

// ======================================================
// 🧩 FEATURE: Central configuration for all metrics
// ======================================================
export const METRIC_CONFIG = {
  ad_recall: { title: "Ad Recall" },
  ad_brand_linkage: { title: "Brand Linkage" },
  ad_diagnostic_1: { title: "Brand Interest" },
  ad_diagnostic_2: { title: "Ad Relevancy" },
  ad_diagnostic_3: { title: "Intent to Use/Acquire Brand" },
  ad_diagnostic_4: { title: "Ease of Understanding" },
  ad_diagnostic_5: { title: "Exposure Sufficiency" },
  ad_diagnostic_6: { title: "Attention Grabbing" },
};

// ======================================================
// 🎨 Enhanced Styles
// ======================================================
const styles = {
  sideNavContainer: {
    width: "260px",
    backgroundColor: "#f8fafc",
    borderRight: "1px solid #e2e8f0",
    padding: "24px 16px",
    display: "flex",
    flexDirection: "column",
    gap: "32px",
    minHeight: "100vh",
    boxShadow: "inset -1px 0 0 rgba(0,0,0,0.05)",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  sectionTitle: {
    fontWeight: 700,
    color: "#475569",
    padding: "0 12px 8px",
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    borderBottom: "1px solid #f1f5f9",
    marginBottom: "4px",
  },
  button: {
    background: "none",
    border: "none",
    padding: "12px 16px",
    borderRadius: "8px",
    textAlign: "left",
    cursor: "pointer",
    fontWeight: 500,
    color: "#475569",
    fontSize: "0.9rem",
    transition: "all 0.2s ease-in-out",
    display: "flex",
    alignItems: "center",
    position: "relative",
  },
  buttonActive: {
    backgroundColor: "#3b82f6",
    color: "#ffffff",
    fontWeight: 600,
    boxShadow: "0 1px 3px rgba(59, 130, 246, 0.3)",
  },
  buttonHover: {
    backgroundColor: "#f1f5f9",
    color: "#1e293b",
  },
  buttonDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    transform: "none",
  },
  manageButton: {
    border: "1px solid #e2e8f0",
    padding: "12px 20px",
    borderRadius: "8px",
    textAlign: "center",
    cursor: "pointer",
    fontWeight: 600,
    color: "#3b82f6",
    fontSize: "0.9rem",
    backgroundColor: "#ffffff",
    transition: "all 0.2s ease-in-out",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    marginBottom: "8px",
  },
  manageButtonHover: {
    backgroundColor: "#3b82f6",
    color: "#ffffff",
    borderColor: "#3b82f6",
    boxShadow: "0 2px 4px rgba(59, 130, 246, 0.3)",
  },
  icon: {
    marginRight: "10px",
    fontSize: "1.1rem",
  },
  activeIndicator: {
    position: "absolute",
    right: "12px",
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "currentColor",
  },
};

// ======================================================
// 🧭 Component: SideNav
// ======================================================
const SideNav = ({
  metric,
  setMetric,
  scoreType,
  setScoreType,
  onManageFiltersClick,
  disabled,
}) => {
  const [hoveredButton, setHoveredButton] = React.useState(null);

  // Enhanced button style with hover effects
  const getButtonStyle = (key, isActive) => ({
    ...styles.button,
    ...(isActive && styles.buttonActive),
    ...(hoveredButton === key && !isActive && !disabled && styles.buttonHover),
    ...(disabled && styles.buttonDisabled),
  });

  const getManageButtonStyle = () => ({
    ...styles.manageButton,
    ...(hoveredButton === 'manage' && !disabled && styles.manageButtonHover),
    ...(disabled && styles.buttonDisabled),
  });

  return (
    <div style={styles.sideNavContainer}>
      {/* ========================== */}
      {/* ⚙️ Manage Filters Button - MOVED TO TOP */}
      {/* ========================== */}
      <div style={styles.section}>
        <button
          onClick={() => !disabled && onManageFiltersClick()}
          style={getManageButtonStyle()}
          disabled={disabled}
          onMouseEnter={() => !disabled && setHoveredButton('manage')}
          onMouseLeave={() => setHoveredButton(null)}
        >
          ⚙️ Manage Filters
        </button>
      </div>

      {/* ========================== */}
      {/* 🎯 Metric Selector Section */}
      {/* ========================== */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Performance Metrics</h3>
        {Object.entries(METRIC_CONFIG).map(([key, config]) => (
          <button
            key={key}
            onClick={() => !disabled && setMetric(key)}
            style={getButtonStyle(`metric-${key}`, metric === key)}
            disabled={disabled}
            onMouseEnter={() => !disabled && setHoveredButton(`metric-${key}`)}
            onMouseLeave={() => setHoveredButton(null)}
          >
            <span style={styles.icon}>
              {metric === key ? "●" : "○"}
            </span>
            {config.title}
            {metric === key && <div style={styles.activeIndicator} />}
          </button>
        ))}
      </div>

      {/* ========================== */}
      {/* 📊 Score Type Section */}
      {/* ========================== */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Score Type</h3>
        
        <button
          onClick={() => !disabled && setScoreType("topBox")}
          style={getButtonStyle("score-topBox", scoreType === "topBox")}
          disabled={disabled}
          onMouseEnter={() => !disabled && setHoveredButton("score-topBox")}
          onMouseLeave={() => setHoveredButton(null)}
        >
          <span style={styles.icon}>
            {scoreType === "topBox" ? "●" : "○"}
          </span>
          Top Box
          {scoreType === "topBox" && <div style={styles.activeIndicator} />}
        </button>

        <button
          onClick={() => !disabled && setScoreType("top2Box")}
          style={getButtonStyle("score-top2Box", scoreType === "top2Box")}
          disabled={disabled}
          onMouseEnter={() => !disabled && setHoveredButton("score-top2Box")}
          onMouseLeave={() => setHoveredButton(null)}
        >
          <span style={styles.icon}>
            {scoreType === "top2Box" ? "●" : "○"}
          </span>
          Top 2 Box
          {scoreType === "top2Box" && <div style={styles.activeIndicator} />}
        </button>
      </div>
    </div>
  );
};

export default SideNav;